import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Briefcase, Plane, Wifi, Check } from "lucide-react";
import { api } from "@/lib/apiClient";

export default function JobDetailPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    current_location: "Berlin, Germany",
    years_experience: 0,
    linkedin_url: "",
    cover_letter: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(null)).finally(() => setLoading(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/applications", { job_id: id, ...form, years_experience: Number(form.years_experience) });
      toast.success("Application submitted", { description: "Check your email for a confirmation." });
      setSubmitted(true);
    } catch (err) {
      toast.error("Failed to submit", { description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-24 text-sm text-[#5C6B64]">Loading…</div>;
  if (!job) return (
    <div className="max-w-7xl mx-auto px-6 py-24">
      <p className="text-sm text-[#5C6B64]">Job not found.</p>
      <Link to="/jobs" className="mt-4 inline-block text-[#D35400]">← Back to jobs</Link>
    </div>
  );

  return (
    <div data-testid="job-detail-page" className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
      <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-2 text-sm text-[#5C6B64] hover:text-[#1A2E24]">
        <ArrowLeft size={16} /> All jobs
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#1A2E24]/10 text-[#1A2E24] px-2.5 py-1">{job.category}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#D35400]/10 text-[#D35400] px-2.5 py-1">{job.job_type}</span>
            {job.visa_sponsorship && <span className="text-[10px] uppercase tracking-[0.2em] font-semibold bg-[#2E4E3F]/10 text-[#2E4E3F] px-2.5 py-1">Visa Sponsored</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#1A2E24]">{job.title}</h1>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Location</div><div className="mt-1 text-[#1A2E24] flex items-center gap-1"><MapPin size={14}/>{job.location}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Salary</div><div className="mt-1 text-[#1A2E24] flex items-center gap-1"><Briefcase size={14}/>{job.salary_range}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Visa</div><div className="mt-1 text-[#1A2E24] flex items-center gap-1"><Plane size={14}/>{job.visa_sponsorship ? "Sponsored" : "None"}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#5C6B64]">Setup</div><div className="mt-1 text-[#1A2E24] flex items-center gap-1"><Wifi size={14}/>{job.remote ? "Remote" : "On-site"}</div></div>
          </div>

          <div className="mt-12">
            <h2 className="font-display text-xl text-[#1A2E24]">About the contract</h2>
            <p className="mt-3 text-[#5C6B64] leading-relaxed">{job.description}</p>
          </div>

          <div className="mt-10">
            <h2 className="font-display text-xl text-[#1A2E24]">What you'll bring</h2>
            <ul className="mt-4 space-y-2">
              {job.requirements?.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-[#1A2E24]"><Check size={16} className="text-[#D35400] mt-0.5 shrink-0"/>{r}</li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <h2 className="font-display text-xl text-[#1A2E24]">Benefits</h2>
            <ul className="mt-4 space-y-2">
              {job.benefits?.map((r) => (
                <li key={r} className="flex items-start gap-2 text-sm text-[#1A2E24]"><Check size={16} className="text-[#D35400] mt-0.5 shrink-0"/>{r}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* APPLY FORM */}
        <div className="lg:col-span-5">
          <div className="sticky top-28 border border-[#DCD7C9] bg-white p-6 sm:p-8">
            {submitted ? (
              <div data-testid="apply-success" className="text-center py-10">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#2E4E3F]/10 text-[#2E4E3F] mb-4">
                  <Check size={26} />
                </div>
                <div className="font-display text-2xl text-[#1A2E24]">Application sent</div>
                <p className="mt-2 text-sm text-[#5C6B64]">We'll be in touch by email shortly.</p>
                <Link to="/jobs" className="mt-6 inline-block text-[#D35400] text-sm">← Browse more jobs</Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="apply-form" className="space-y-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D35400]">Apply now</div>
                  <div className="mt-2 font-display text-xl text-[#1A2E24]">Send your application to Berlin</div>
                </div>
                {[
                  { name: "full_name", label: "Full name", type: "text", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Phone", type: "tel", required: true },
                  { name: "current_location", label: "Current location", type: "text", required: true },
                  { name: "years_experience", label: "Years of experience", type: "number", required: true },
                  { name: "linkedin_url", label: "LinkedIn (optional)", type: "url", required: false },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="text-xs uppercase tracking-widest text-[#5C6B64]">{f.label}</label>
                    <input
                      data-testid={`apply-${f.name}`}
                      required={f.required}
                      type={f.type}
                      value={form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
                    />
                  </div>
                ))}
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#5C6B64]">Why this contract?</label>
                  <textarea
                    data-testid="apply-cover_letter"
                    required
                    rows={5}
                    value={form.cover_letter}
                    onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                    className="mt-1 w-full border border-[#DCD7C9] px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#1A2E24]"
                    placeholder="A few honest sentences about why you want to move to the US and take this on…"
                  />
                </div>
                <button
                  data-testid="apply-submit"
                  disabled={submitting}
                  className="w-full bg-[#1A2E24] text-[#F9F6F0] py-3.5 text-sm font-medium hover:bg-[#2C4A3B] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Submit application"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
